/**
 * Level19Scene - 关卡19：黑洞视界
 * @description 教学目标：理解事件视界、时间膨胀和引力红移的极端效应
 * 核心公式：Rs = 2GM/c²（史瓦西半径）
 * 引力红移：z = 1/√(1-Rs/r) - 1
 * 时间膨胀：接近视界时时间趋于无限慢
 */

import Phaser from 'phaser';
import { Vec2 } from 'planck';
import type { LevelConfig } from '@/levels/LevelConfig.ts';
import { TutorialUI } from '@/ui/TutorialUI.ts';
import { getTutorial } from '@/data/tutorials.ts';
import { getGameState } from '@/core/GameState.ts';
import { Blackhole, type GravitationalRedshiftResult, type GravitationalPotentialResult } from '@/physics/Blackhole.ts';

// 关卡19特定配置
interface Level19SpecificConfig {
  /** 黑洞位置 */
  blackholePosition: { x: number; y: number };
  /** 黑洞质量 */
  blackholeMass: number;
  /** 视界半径因子 */
  schwarzschildRadiusFactor: number;
  /** 数据收集区域半径 */
  dataCollectionRadius: number;
  /** 安全距离 */
  safeDistance: number;
  /** 目标数据点数量 */
  targetDataPoints: number;
  /** 每个数据点收集时间 */
  dataCollectionTime: number;
  /** 公式 */
  formula: string;
  /** 提示 */
  hint: string;
  /** 红移公式 */
  redshiftFormula: string;
}

// 游戏状态
type GameState = 'idle' | 'running' | 'collecting' | 'success' | 'failed' | 'captured';

// 游戏尺寸常量
const GAME_WIDTH = 800;
const GAME_HEIGHT = 600;

/**
 * Level19Scene 类
 * 关卡19场景，实现黑洞视界探索
 */
export class Level19Scene extends Phaser.Scene {
  // 关卡配置
  private levelConfig: LevelConfig | null = null;
  private levelSpecific: Level19SpecificConfig | null = null;

  // 游戏状态
  private gameState: GameState = 'idle';
  private elapsedTime: number = 0;
  private localTime: number = 0;
  private remoteTime: number = 0;
  private collectedDataPoints: number = 0;
  private currentCollectionProgress: number = 0;
  private timeDilationFactor: number = 1;

  // 黑洞物理
  private blackhole: Blackhole | null = null;
  private schwarzschildRadius: number = 0;

  // 探测器状态
  private probePosition: Vec2;
  private probeVelocity: Vec2;
  private probeGraphics: Phaser.GameObjects.Graphics | null = null;

  // 游戏对象
  private blackholeGraphics: Phaser.GameObjects.Graphics | null = null;
  private eventHorizonGraphics: Phaser.GameObjects.Graphics | null = null;
  private stars: Phaser.GameObjects.Graphics | null = null;
  private lightRays: Phaser.GameObjects.Graphics | null = null;
  private warningGraphics: Phaser.GameObjects.Graphics | null = null;
  private collectionZoneGraphics: Phaser.GameObjects.Graphics | null = null;

  // UI 元素
  private hintText: Phaser.GameObjects.Text | null = null;
  private startButton: Phaser.GameObjects.Graphics | null = null;
  private resultText: Phaser.GameObjects.Text | null = null;
  private redshiftDisplay: Phaser.GameObjects.Text | null = null;
  private timeDilationDisplay: Phaser.GameObjects.Text | null = null;
  private distanceDisplay: Phaser.GameObjects.Text | null = null;
  private dataProgressDisplay: Phaser.GameObjects.Text | null = null;
  private warningText: Phaser.GameObjects.Text | null = null;
  private localTimeDisplay: Phaser.GameObjects.Text | null = null;
  private remoteTimeDisplay: Phaser.GameObjects.Text | null = null;

  // 教程组件
  private tutorialUI: TutorialUI | null = null;

  // 红移颜色效果
  private currentRedshift: number = 0;
  private baseProbeColor: number = 0x4ade80;

  constructor() {
    super({ key: 'Level19Scene' });
    this.probePosition = Vec2(600, 300);
    this.probeVelocity = Vec2(0, 0);
  }

  preload(): void {
    // 加载关卡配置
    this.load.json('level-19', 'src/levels/levels/level-19.json');
  }

  create(): void {
    // 加载关卡配置
    this.levelConfig = this.cache.json.get('level-19') as LevelConfig;

    if (this.levelConfig && this.levelConfig.levelSpecific) {
      this.levelSpecific = this.levelConfig.levelSpecific as unknown as Level19SpecificConfig;
    }

    // 初始化黑洞
    this.initBlackhole();

    // 初始化探测器位置
    if (this.levelConfig?.player?.startPosition) {
      this.probePosition = Vec2(
        this.levelConfig.player.startPosition.x,
        this.levelConfig.player.startPosition.y
      );
    }

    // 创建背景（深空场景）
    this.createBackground();

    // 创建黑洞和事件视界
    this.createBlackholeVisualization();

    // 创建探测器
    this.createProbe();

    // 创建 UI
    this.createUI();

    // 设置输入事件
    this.setupInputEvents();

    // 显示教程
    this.showTutorial();
  }

  override update(_time: number, delta: number): void {
    if (this.gameState !== 'running' && this.gameState !== 'collecting') return;

    // 更新物理（帧率独立）
    const deltaTime = delta / 1000;

    // 应用引力加速度
    if (this.blackhole) {
      const gravitationalAcc = this.blackhole.calculateGravitationalAcceleration(this.probePosition);
      
      // 更新速度（考虑时间膨胀）
      const effectiveDeltaTime = deltaTime / this.timeDilationFactor;
      this.probeVelocity = Vec2(
        this.probeVelocity.x + gravitationalAcc.x * effectiveDeltaTime,
        this.probeVelocity.y + gravitationalAcc.y * effectiveDeltaTime
      );

      // 更新位置
      this.probePosition = Vec2(
        this.probePosition.x + this.probeVelocity.x * effectiveDeltaTime,
        this.probePosition.y + this.probeVelocity.y * effectiveDeltaTime
      );
    }

    // 更新时间显示（时间膨胀效果）
    this.updateTimeEffect(deltaTime);

    // 计算当前物理状态
    this.updatePhysicsState();

    // 更新视觉效果
    this.updateVisualEffects();

    // 检测游戏状态
    this.checkGameState();
  }

  /**
   * 初始化黑洞
   */
  private initBlackhole(): void {
    if (!this.levelSpecific) return;

    const position = Vec2(
      this.levelSpecific.blackholePosition.x,
      this.levelSpecific.blackholePosition.y
    );

    this.blackhole = new Blackhole({
      id: 'main-blackhole',
      position,
      mass: this.levelSpecific.blackholeMass,
    });

    this.schwarzschildRadius = this.blackhole.getSchwarzschildRadius();
  }

  /**
   * 创建背景（深空场景）
   */
  private createBackground(): void {
    // 深空背景
    this.cameras.main.setBackgroundColor('#000010');

    // 创建星星
    this.stars = this.add.graphics();
    for (let i = 0; i < 150; i++) {
      const x = Math.random() * GAME_WIDTH;
      const y = Math.random() * GAME_HEIGHT;
      const size = Math.random() * 2 + 0.5;
      const brightness = Math.random() * 0.5 + 0.5;

      this.stars.fillStyle(0xffffff, brightness);
      this.stars.fillCircle(x, y, size);
    }

    // 添加引力透镜效果的光线
    this.createGravitationalLensingRays();
  }

  /**
   * 创建引力透镜光线效果
   */
  private createGravitationalLensingRays(): void {
    this.lightRays = this.add.graphics();
    
    if (!this.blackhole) return;

    const position = this.blackhole.getPosition();
    
    // 绘制多条弯曲光线（简化表示）
    const rayCount = 8;
    for (let i = 0; i < rayCount; i++) {
      const startAngle = (i / rayCount) * Math.PI * 2;
      const startX = position.x + Math.cos(startAngle) * 350;
      const startY = position.y + Math.sin(startAngle) * 350;
      
      // 追踪光线
      const direction = Vec2(-Math.cos(startAngle), -Math.sin(startAngle));
      const result = this.blackhole.traceLightRay(
        Vec2(startX, startY),
        direction,
        { maxSteps: 100, stepSize: 10 }
      );
      
      // 绘制光线路径
      this.lightRays?.lineStyle(1, 0x4a90d9, 0.3);
      this.lightRays?.beginPath();
      
      const path = result.path;
      if (path.length > 0 && path[0]) {
        this.lightRays?.moveTo(path[0].x, path[0].y);
        for (let j = 1; j < Math.min(path.length, 50); j++) {
          const point = path[j];
          if (point) {
            this.lightRays?.lineTo(point.x, point.y);
          }
        }
      }
      this.lightRays?.strokePath();
    }
  }

  /**
   * 创建黑洞可视化
   */
  private createBlackholeVisualization(): void {
    if (!this.blackhole || !this.levelSpecific) return;

    const position = this.blackhole.getPosition();
    const rs = this.schwarzschildRadius;
    const dataRadius = this.levelSpecific.dataCollectionRadius;

    // 黑洞核心（纯黑）
    this.blackholeGraphics = this.add.graphics();
    this.blackholeGraphics.fillStyle(0x000000, 1);
    this.blackholeGraphics.fillCircle(position.x, position.y, rs * this.levelSpecific.schwarzschildRadiusFactor);
    
    // 黑洞吸积盘效果
    this.blackholeGraphics.lineStyle(3, 0xff6b00, 0.8);
    for (let r = rs * 2; r < rs * 4; r += 5) {
      this.blackholeGraphics.strokeCircle(position.x, position.y, r);
    }

    // 事件视界边界（红色警告线）
    this.eventHorizonGraphics = this.add.graphics();
    this.eventHorizonGraphics.lineStyle(4, 0xff0000, 0.8);
    this.eventHorizonGraphics.strokeCircle(position.x, position.y, rs);
    
    // 事件视界标签
    this.add.text(position.x, position.y - rs - 25, '⚠️ 事件视界', {
      fontSize: '14px',
      color: '#ff0000',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // 数据收集区域
    this.collectionZoneGraphics = this.add.graphics();
    this.collectionZoneGraphics.lineStyle(2, 0x4ade80, 0.6);
    this.collectionZoneGraphics.strokeCircle(position.x, position.y, rs + dataRadius);
    
    // 数据收集区域标签
    this.add.text(position.x, position.y + rs + dataRadius + 15, '📊 数据收集区', {
      fontSize: '12px',
      color: '#4ade80',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 安全距离标记
    const safeDistance = this.levelSpecific.safeDistance;
    this.collectionZoneGraphics.lineStyle(1, 0x4ade80, 0.3);
    this.collectionZoneGraphics.strokeCircle(position.x, position.y, safeDistance);
  }

  /**
   * 创建探测器
   */
  private createProbe(): void {
    this.probeGraphics = this.add.graphics();
    this.drawProbe();
  }

  /**
   * 绘制探测器
   */
  private drawProbe(): void {
    if (!this.probeGraphics) return;

    this.probeGraphics.clear();

    const x = this.probePosition.x;
    const y = this.probePosition.y;

    // 应用红移颜色效果
    const color = this.getRedshiftColor(this.currentRedshift);

    // 探测器主体
    this.probeGraphics.fillStyle(color, 1);
    this.probeGraphics.fillCircle(x, y, 12);

    // 探测器天线
    this.probeGraphics.lineStyle(2, color);
    this.probeGraphics.beginPath();
    this.probeGraphics.moveTo(x, y - 12);
    this.probeGraphics.lineTo(x, y - 20);
    this.probeGraphics.lineTo(x - 5, y - 25);
    this.probeGraphics.moveTo(x, y - 20);
    this.probeGraphics.lineTo(x + 5, y - 25);
    this.probeGraphics.strokePath();

    // 探测器信号指示灯（闪烁）
    if (this.gameState === 'collecting') {
      this.probeGraphics.fillStyle(0xffff00, 1);
      this.probeGraphics.fillCircle(x, y - 22, 3);
    }
  }

  /**
   * 根据红移值计算颜色
   */
  private getRedshiftColor(redshift: number): number {
    // 红移越大，颜色越红
    // 红移 = 0: 原色绿色 (0x4ade80)
    // 红移 > 0: 向红色偏移 (0xff6b6b)
    
    if (redshift <= 0) return this.baseProbeColor;
    if (redshift >= 10) return 0xff6b6b;

    // 线性插值
    const ratio = Math.min(redshift / 10, 1);
    
    // 从绿色到红色的渐变
    const greenComponent = Math.floor(0xde * (1 - ratio));
    const redComponent = Math.floor(0x4a + (0xff - 0x4a) * ratio);
    const blueComponent = Math.floor(0x80 * (1 - ratio));

    return (redComponent << 16) | (greenComponent << 8) | blueComponent;
  }

  /**
   * 创建 UI
   */
  private createUI(): void {
    // 公式显示
    this.add.text(GAME_WIDTH / 2, 25, '事件视界公式：Rs = 2GM/c²', {
      fontSize: '22px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // 红移公式
    this.add.text(GAME_WIDTH / 2, 50, '引力红移：z = 1/√(1-Rs/r) - 1', {
      fontSize: '14px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 物理状态显示区域
    this.createPhysicsDisplay();

    // 时间显示
    this.createTimeDisplay();

    // 数据收集进度
    this.createDataProgressDisplay();

    // 提示文本
    this.hintText = this.add.text(
      GAME_WIDTH / 2,
      85,
      this.levelSpecific?.hint ?? '控制探测器接近黑洞视界边缘收集数据',
      {
        fontSize: '12px',
        color: '#888888',
        fontFamily: 'Arial',
      }
    ).setOrigin(0.5);

    // 警告文本（初始隐藏）
    this.warningText = this.add.text(GAME_WIDTH / 2, 120, '', {
      fontSize: '16px',
      color: '#ff6b6b',
      fontFamily: 'Arial',
      fontStyle: 'bold',
      backgroundColor: '#1a0000',
      padding: { x: 10, y: 5 },
    }).setOrigin(0.5).setVisible(false);

    // 开始按钮
    this.createStartButton();

    // 结果文本（初始隐藏）
    this.resultText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, '', {
      fontSize: '24px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
      backgroundColor: '#1a1a3a',
      padding: { x: 20, y: 10 },
    }).setOrigin(0.5).setVisible(false).setDepth(10);

    // 物理说明区域
    this.createPhysicsInfo();

    // 操作说明
    this.createControlsInfo();
  }

  /**
   * 创建物理状态显示
   */
  private createPhysicsDisplay(): void {
    // 物理状态框
    const physicsBox = this.add.graphics();
    physicsBox.fillStyle(0x1a1a3a, 0.8);
    physicsBox.fillRect(10, 130, 180, 150);
    physicsBox.lineStyle(1, 0x4a4a6a);
    physicsBox.strokeRect(10, 130, 180, 150);

    this.add.text(20, 140, '📡 物理状态', {
      fontSize: '14px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    });

    // 距离显示
    this.distanceDisplay = this.add.text(20, 165, '距离：--- 单位', {
      fontSize: '12px',
      color: '#ffffff',
      fontFamily: 'Arial',
    });

    // 红移显示
    this.redshiftDisplay = this.add.text(20, 185, '红移 z：---', {
      fontSize: '12px',
      color: '#ff6b6b',
      fontFamily: 'Arial',
    });

    // 时间膨胀显示
    this.timeDilationDisplay = this.add.text(20, 205, '时间膨胀：γ = 1.00', {
      fontSize: '12px',
      color: '#6b8cff',
      fontFamily: 'Arial',
    });

    // 安全状态显示
    this.add.text(20, 225, '安全等级：---', {
      fontSize: '12px',
      color: '#4ade80',
      fontFamily: 'Arial',
    });
  }

  /**
   * 创建时间显示
   */
  private createTimeDisplay(): void {
    // 时间显示框
    const timeBox = this.add.graphics();
    timeBox.fillStyle(0x1a1a3a, 0.8);
    timeBox.fillRect(GAME_WIDTH - 190, 130, 180, 80);
    timeBox.lineStyle(1, 0x4a4a6a);
    timeBox.strokeRect(GAME_WIDTH - 190, 130, 180, 80);

    this.add.text(GAME_WIDTH - 180, 140, '⏰ 时间效应', {
      fontSize: '14px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    });

    // 远程时间（远离黑洞的参考时间）
    this.remoteTimeDisplay = this.add.text(GAME_WIDTH - 180, 165, '远程时间：0.00 秒', {
      fontSize: '12px',
      color: '#ffffff',
      fontFamily: 'Arial',
    });

    // 本地时间（探测器经历的时间）
    this.localTimeDisplay = this.add.text(GAME_WIDTH - 180, 185, '本地时间：0.00 秒', {
      fontSize: '12px',
      color: '#ffa500',
      fontFamily: 'Arial',
    });
  }

  /**
   * 创建数据收集进度显示
   */
  private createDataProgressDisplay(): void {
    // 数据进度框
    const progressBox = this.add.graphics();
    progressBox.fillStyle(0x1a1a3a, 0.8);
    progressBox.fillRect(GAME_WIDTH / 2 - 100, 450, 200, 60);
    progressBox.lineStyle(2, 0x4ade80);
    progressBox.strokeRect(GAME_WIDTH / 2 - 100, 450, 200, 60);

    this.add.text(GAME_WIDTH / 2, 460, '📊 数据收集进度', {
      fontSize: '14px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.dataProgressDisplay = this.add.text(
      GAME_WIDTH / 2,
      490,
      `已收集：0/${this.levelSpecific?.targetDataPoints ?? 3} 数据点`,
      {
        fontSize: '12px',
        color: '#ffffff',
        fontFamily: 'Arial',
      }
    ).setOrigin(0.5);
  }

  /**
   * 创建开始按钮
   */
  private createStartButton(): void {
    this.startButton = this.add.graphics();
    this.startButton.fillStyle(0x4ade80, 1);
    this.startButton.fillRect(GAME_WIDTH / 2 - 60, 520, 120, 40);
    this.startButton.lineStyle(2, 0x4ade80);
    this.startButton.strokeRect(GAME_WIDTH / 2 - 60, 520, 120, 40);

    this.add.text(GAME_WIDTH / 2, 540, '开始探索', {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.startButton.setInteractive(
      new Phaser.Geom.Rectangle(GAME_WIDTH / 2 - 60, 520, 120, 40),
      Phaser.Geom.Rectangle.Contains
    );
    this.startButton.on('pointerdown', () => this.startGame());

    this.startButton.on('pointerover', () => {
      if (this.gameState === 'idle') {
        this.startButton?.clear();
        this.startButton?.fillStyle(0x5aee90, 1);
        this.startButton?.fillRect(GAME_WIDTH / 2 - 60, 520, 120, 40);
        this.startButton?.strokeRect(GAME_WIDTH / 2 - 60, 520, 120, 40);
      }
    });

    this.startButton.on('pointerout', () => {
      if (this.gameState === 'idle') {
        this.startButton?.clear();
        this.startButton?.fillStyle(0x4ade80, 1);
        this.startButton?.fillRect(GAME_WIDTH / 2 - 60, 520, 120, 40);
        this.startButton?.strokeRect(GAME_WIDTH / 2 - 60, 520, 120, 40);
      }
    });
  }

  /**
   * 创建物理信息显示
   */
  private createPhysicsInfo(): void {
    const infoBox = this.add.graphics();
    infoBox.fillStyle(0x1a1a3a, 0.6);
    infoBox.fillRect(10, GAME_HEIGHT - 70, 780, 60);

    this.add.text(20, GAME_HEIGHT - 60, '💡 物理原理', {
      fontSize: '12px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    });

    this.add.text(20, GAME_HEIGHT - 45, '事件视界：Rs = 2GM/c²，光无法逃逸的边界。越接近视界，引力红移越大，时间越慢。', {
      fontSize: '10px',
      color: '#ffffff',
      fontFamily: 'Arial',
    });

    this.add.text(20, GAME_HEIGHT - 30, '引力红移：从视界边缘发出的光会被极度红移，时间膨胀趋于无限。', {
      fontSize: '10px',
      color: '#888888',
      fontFamily: 'Arial',
    });
  }

  /**
   * 创建操作说明
   */
  private createControlsInfo(): void {
    const controlsBox = this.add.graphics();
    controlsBox.fillStyle(0x1a1a3a, 0.6);
    controlsBox.fillRect(GAME_WIDTH - 250, GAME_HEIGHT - 70, 240, 60);

    this.add.text(GAME_WIDTH - 240, GAME_HEIGHT - 60, '🎮 操作说明', {
      fontSize: '12px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    });

    this.add.text(GAME_WIDTH - 240, GAME_HEIGHT - 45, '方向键/WASD：控制探测器移动', {
      fontSize: '10px',
      color: '#ffffff',
      fontFamily: 'Arial',
    });

    this.add.text(GAME_WIDTH - 240, GAME_HEIGHT - 30, '空格键：在数据收集区收集数据', {
      fontSize: '10px',
      color: '#888888',
      fontFamily: 'Arial',
    });
  }

  /**
   * 设置输入事件
   */
  private setupInputEvents(): void {
    // 键盘控制
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (this.gameState !== 'running' && this.gameState !== 'collecting') {
        if (event.key === 'Enter') {
          this.startGame();
        }
        if (event.key === 'r' || event.key === 'R') {
          this.resetGame();
        }
        return;
      }

      // 移动控制
      const moveSpeed = 5;
      switch (event.key) {
        case 'ArrowUp':
        case 'w':
        case 'W':
          this.probeVelocity = Vec2(this.probeVelocity.x, -moveSpeed);
          break;
        case 'ArrowDown':
        case 's':
        case 'S':
          this.probeVelocity = Vec2(this.probeVelocity.x, moveSpeed);
          break;
        case 'ArrowLeft':
        case 'a':
        case 'A':
          this.probeVelocity = Vec2(-moveSpeed, this.probeVelocity.y);
          break;
        case 'ArrowRight':
        case 'd':
        case 'D':
          this.probeVelocity = Vec2(moveSpeed, this.probeVelocity.y);
          break;
        case ' ':
          // 尝试收集数据
          this.tryCollectData();
          break;
        case 'r':
        case 'R':
          this.resetGame();
          break;
      }
    });

    // 鼠标/触摸控制
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.gameState === 'running' || this.gameState === 'collecting') {
        // 计算指向指针的方向
        const dx = pointer.x - this.probePosition.x;
        const dy = pointer.y - this.probePosition.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance > 10) {
          const moveSpeed = 8;
          this.probeVelocity = Vec2(
            (dx / distance) * moveSpeed,
            (dy / distance) * moveSpeed
          );
        }
      }
    });
  }

  /**
   * 开始游戏
   */
  private startGame(): void {
    if (this.gameState !== 'idle') return;

    this.gameState = 'running';
    this.elapsedTime = 0;
    this.localTime = 0;
    this.remoteTime = 0;
    this.collectedDataPoints = 0;
    this.currentCollectionProgress = 0;
    this.timeDilationFactor = 1;
    this.currentRedshift = 0;

    // 重置探测器位置
    if (this.levelConfig?.player?.startPosition) {
      this.probePosition = Vec2(
        this.levelConfig.player.startPosition.x,
        this.levelConfig.player.startPosition.y
      );
    }
    this.probeVelocity = Vec2(0, 0);

    // 更新按钮状态
    this.updateStartButtonState('running');

    // 隐藏结果和警告
    this.resultText?.setVisible(false);
    this.warningText?.setVisible(false);

    // 更新提示
    this.hintText?.setText('使用方向键或点击控制探测器移动，接近黑洞收集数据');
  }

  /**
   * 更新开始按钮状态
   */
  private updateStartButtonState(state: GameState): void {
    if (!this.startButton) return;

    this.startButton.clear();

    const color = state === 'idle' ? 0x4ade80 : 0x6a6a8a;
    this.startButton.fillStyle(color, 1);
    this.startButton.fillRect(GAME_WIDTH / 2 - 60, 520, 120, 40);
    this.startButton.strokeRect(GAME_WIDTH / 2 - 60, 520, 120, 40);
  }

  /**
   * 更新时间效应
   */
  private updateTimeEffect(deltaTime: number): void {
    // 远程时间正常流逝
    this.remoteTime += deltaTime;

    // 本地时间根据时间膨胀因子流逝
    this.localTime += deltaTime / this.timeDilationFactor;

    // 更新显示
    this.remoteTimeDisplay?.setText(`远程时间：${this.remoteTime.toFixed(2)} 秒`);
    this.localTimeDisplay?.setText(`本地时间：${this.localTime.toFixed(2)} 秒`);
  }

  /**
   * 更新物理状态
   */
  private updatePhysicsState(): void {
    if (!this.blackhole) return;

    const distance = this.blackhole.calculateDistance(this.probePosition);
    const rs = this.schwarzschildRadius;

    // 更新距离显示
    this.distanceDisplay?.setText(`距离：${distance.toFixed(1)} 单位`);

    // 计算引力红移
    if (distance > rs) {
      const redshiftResult: GravitationalRedshiftResult = 
        this.blackhole.calculateGravitationalRedshift(this.probePosition);
      this.currentRedshift = redshiftResult.redshift;
      
      // 更新红移显示
      if (redshiftResult.redshift === Infinity) {
        this.redshiftDisplay?.setText('红移 z：∞（视界边缘）');
      } else {
        this.redshiftDisplay?.setText(`红移 z：${redshiftResult.redshift.toFixed(3)}`);
      }

      // 计算时间膨胀因子（简化：使用引力势能计算）
      const potentialResult: GravitationalPotentialResult = 
        this.blackhole.calculateGravitationalPotential(this.probePosition);
      
      // 时间膨胀因子近似：γ ≈ 1 + |Φ|/c²（引力时间膨胀）
      const escapeVelocity = potentialResult.escapeVelocity;
      this.timeDilationFactor = Math.sqrt(1 + escapeVelocity * escapeVelocity);
      
      this.timeDilationDisplay?.setText(`时间膨胀：γ = ${this.timeDilationFactor.toFixed(3)}`);
    } else {
      this.currentRedshift = Infinity;
      this.timeDilationFactor = Infinity;
      this.redshiftDisplay?.setText('红移 z：∞（事件视界内）');
      this.timeDilationDisplay?.setText('时间膨胀：γ = ∞');
    }

    // 更新数据收集进度
    this.updateDataCollectionProgress();
  }

  /**
   * 更新数据收集进度
   */
  private updateDataCollectionProgress(): void {
    if (!this.blackhole || !this.levelSpecific) return;

    const distance = this.blackhole.calculateDistance(this.probePosition);
    const rs = this.schwarzschildRadius;
    const dataRadius = this.levelSpecific.dataCollectionRadius;
    const isInCollectionZone = distance >= rs && distance <= rs + dataRadius;

    if (isInCollectionZone && this.gameState === 'collecting') {
      // 正在收集数据
      this.currentCollectionProgress += 1 / (this.levelSpecific.dataCollectionTime * 60);
      
      if (this.currentCollectionProgress >= 1) {
        this.collectedDataPoints++;
        this.currentCollectionProgress = 0;
        this.gameState = 'running';
        
        // 更新显示
        this.dataProgressDisplay?.setText(
          `已收集：${this.collectedDataPoints}/${this.levelSpecific.targetDataPoints} 数据点`
        );

        // 检查是否完成
        if (this.collectedDataPoints >= this.levelSpecific.targetDataPoints) {
          this.gameState = 'success';
          this.showSuccessResult();
        } else {
          this.hintText?.setText(`数据点 ${this.collectedDataPoints} 已收集！继续收集更多数据`);
        }
      }
    }

    // 更新进度条显示
    if (this.gameState === 'collecting') {
      const progressPercent = Math.floor(this.currentCollectionProgress * 100);
      this.dataProgressDisplay?.setText(
        `收集进度：${progressPercent}% (${this.collectedDataPoints}/${this.levelSpecific.targetDataPoints})`
      );
    }
  }

  /**
   * 尝试收集数据
   */
  private tryCollectData(): void {
    if (!this.blackhole || !this.levelSpecific) return;
    if (this.gameState !== 'running') return;

    const distance = this.blackhole.calculateDistance(this.probePosition);
    const rs = this.schwarzschildRadius;
    const dataRadius = this.levelSpecific.dataCollectionRadius;
    const isInCollectionZone = distance >= rs && distance <= rs + dataRadius;

    if (isInCollectionZone) {
      this.gameState = 'collecting';
      this.currentCollectionProgress = 0;
      this.hintText?.setText('正在收集数据...保持位置！');
    } else {
      this.hintText?.setText('请在数据收集区域内（绿色圈内）按空格收集数据');
    }
  }

  /**
   * 更新视觉效果
   */
  private updateVisualEffects(): void {
    // 更新探测器
    this.drawProbe();

    // 更新警告效果
    this.updateWarningEffects();
  }

  /**
   * 更新警告效果
   */
  private updateWarningEffects(): void {
    if (!this.blackhole || !this.levelSpecific) return;

    const distance = this.blackhole.calculateDistance(this.probePosition);
    const rs = this.schwarzschildRadius;
    const dangerThreshold = rs * 2;

    // 清除旧的警告效果
    if (this.warningGraphics) {
      this.warningGraphics.clear();
    } else {
      this.warningGraphics = this.add.graphics();
    }

    // 根据距离显示警告
    if (distance <= dangerThreshold && distance > rs) {
      // 危险区域警告
      const dangerLevel = 1 - (distance - rs) / (dangerThreshold - rs);
      
      // 闪烁警告圈
      const flashAlpha = 0.3 + dangerLevel * 0.5 * Math.sin(this.elapsedTime * 10);
      this.warningGraphics.lineStyle(3, 0xff0000, flashAlpha);
      this.warningGraphics.strokeCircle(this.probePosition.x, this.probePosition.y, 20);

      // 显示警告文本
      if (distance <= rs * 1.5) {
        this.warningText?.setText('⚠️ 极度危险！接近事件视界！').setVisible(true);
      } else {
        this.warningText?.setText('⚠️ 危险区域！请小心控制').setVisible(true);
      }
    } else if (distance <= rs) {
      // 进入事件视界
      this.warningText?.setText('☠️ 已进入事件视界！无法返回！').setVisible(true);
      this.warningGraphics.fillStyle(0xff0000, 0.8);
      this.warningGraphics.fillCircle(this.probePosition.x, this.probePosition.y, 25);
    } else {
      this.warningText?.setVisible(false);
    }

    // 更新安全状态显示
    const safeDistance = this.levelSpecific.safeDistance;
    if (distance > safeDistance) {
      // 安全
    } else if (distance > dangerThreshold) {
      // 警戒区域
    } else if (distance > rs) {
      // 危险区域
    } else {
      // 事件视界内
    }
  }

  /**
   * 检测游戏状态
   */
  private checkGameState(): void {
    if (!this.blackhole) return;

    // 检测是否进入事件视界
    if (this.blackhole.isInsideEventHorizon(this.probePosition)) {
      this.gameState = 'captured';
      this.showCapturedResult();
    }

    // 检测是否超出边界
    if (
      this.probePosition.x < 0 ||
      this.probePosition.x > GAME_WIDTH ||
      this.probePosition.y < 0 ||
      this.probePosition.y > GAME_HEIGHT
    ) {
      // 探测器飞出边界，重置到安全位置
      if (this.levelConfig?.player?.startPosition) {
        this.probePosition = Vec2(
          this.levelConfig.player.startPosition.x,
          this.levelConfig.player.startPosition.y
        );
      }
      this.probeVelocity = Vec2(0, 0);
      this.hintText?.setText('探测器飞出边界，已返回安全位置');
    }
  }

  /**
   * 显示成功结果
   */
  private showSuccessResult(): void {
    const gameState = getGameState();
    gameState.completeLevel(19, this.elapsedTime);

    this.resultText?.setText(
      `🎉 成功！\n` +
      `收集了 ${this.collectedDataPoints} 个数据点\n` +
      `最大红移观测：z=${this.currentRedshift.toFixed(2)}\n` +
      `时间膨胀因子：γ=${this.timeDilationFactor.toFixed(2)}`
    )
      .setColor('#4ade80')
      .setVisible(true);

    this.hintText?.setText('成功收集黑洞视界边缘的数据！了解了极端引力效应');

    this.updateStartButtonState('idle');
    this.createCompletionButtons();
  }

  /**
   * 显示被捕获结果（失败）
   */
  private showCapturedResult(): void {
    this.resultText?.setText(
      `☠️ 被黑洞捕获！\n` +
      `探测器进入事件视界\n` +
      `无法逃脱引力束缚\n` +
      `时间在视界边缘无限延长`
    )
      .setColor('#ff6b6b')
      .setVisible(true);

    this.hintText?.setText('教训：一旦进入事件视界，任何物体都无法逃脱！请重新尝试');

    this.updateStartButtonState('idle');
    this.createCompletionButtons();
  }

  /**
   * 创建完成后的按钮
   */
  private createCompletionButtons(): void {
    const y = GAME_HEIGHT / 2 + 100;

    const nextBtn = this.add.text(GAME_WIDTH / 2 - 80, y, '下一关', {
      fontSize: '18px',
      color: '#4ade80',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    nextBtn.on('pointerdown', () => {
      const gameState = getGameState();
      if (gameState.isLevelUnlocked(20)) {
        this.scene.start('Level20Scene');
      } else {
        this.scene.start('LevelSelectScene');
      }
    });

    const backBtn = this.add.text(GAME_WIDTH / 2 + 80, y, '返回', {
      fontSize: '18px',
      color: '#888888',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    backBtn.on('pointerdown', () => {
      this.scene.start('LevelSelectScene');
    });

    const retryBtn = this.add.text(GAME_WIDTH / 2, y + 40, '重新尝试', {
      fontSize: '16px',
      color: '#ffa500',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    retryBtn.on('pointerdown', () => {
      this.resetGame();
    });
  }

  /**
   * 重置游戏
   */
  private resetGame(): void {
    this.gameState = 'idle';
    this.elapsedTime = 0;
    this.localTime = 0;
    this.remoteTime = 0;
    this.collectedDataPoints = 0;
    this.currentCollectionProgress = 0;
    this.timeDilationFactor = 1;
    this.currentRedshift = 0;

    // 重置探测器位置
    if (this.levelConfig?.player?.startPosition) {
      this.probePosition = Vec2(
        this.levelConfig.player.startPosition.x,
        this.levelConfig.player.startPosition.y
      );
    }
    this.probeVelocity = Vec2(0, 0);

    // 清除警告
    if (this.warningGraphics) {
      this.warningGraphics.clear();
    }

    // 隐藏结果和警告
    this.resultText?.setVisible(false);
    this.warningText?.setVisible(false);

    // 重置提示
    this.hintText?.setText(this.levelSpecific?.hint ?? '控制探测器接近黑洞视界边缘收集数据');

    // 更新显示
    this.dataProgressDisplay?.setText(
      `已收集：0/${this.levelSpecific?.targetDataPoints ?? 3} 数据点`
    );
    this.remoteTimeDisplay?.setText('远程时间：0.00 秒');
    this.localTimeDisplay?.setText('本地时间：0.00 秒');
    this.distanceDisplay?.setText('距离：--- 单位');
    this.redshiftDisplay?.setText('红移 z：---');
    this.timeDilationDisplay?.setText('时间膨胀：γ = 1.00');

    // 更新按钮状态
    this.updateStartButtonState('idle');

    // 重绘探测器
    this.drawProbe();
  }

  /**
   * 显示教程
   */
  private showTutorial(): void {
    const tutorialData = getTutorial('level-19');
    if (!tutorialData) return;

    this.tutorialUI = new TutorialUI(this, {
      levelNumber: 19,
      tutorialData,
      onStart: () => {
        this.tutorialUI?.destroy();
        this.tutorialUI = null;
      },
      onSkip: () => {
        this.tutorialUI?.destroy();
        this.tutorialUI = null;
      },
    });
  }

  /**
   * 获取当前游戏状态
   */
  getGameState(): GameState {
    return this.gameState;
  }

  /**
   * 获取探测器位置
   */
  getProbePosition(): Vec2 {
    return this.probePosition;
  }

  /**
   * 获取当前红移值
   */
  getCurrentRedshift(): number {
    return this.currentRedshift;
  }

  /**
   * 获取时间膨胀因子
   */
  getTimeDilationFactor(): number {
    return this.timeDilationFactor;
  }

  /**
   * 获取已收集的数据点数量
   */
  getCollectedDataPoints(): number {
    return this.collectedDataPoints;
  }
}